import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import {
  ArrowUpRight,
  Boxes,
  Container,
  BadgeDollarSign,
  Clock,
  Ship,
  Gauge,
  FileDown,
  FileText,
  Square,
  Package,
  MessageCircle,
  ChevronDown,
} from "lucide-react";
import SEO from "./SEO";
import { Reveal, Stagger, StaggerItem } from "./Motion";
import { waLink, PARTNER_CAPACITY, SPECIFICATION_QUALIFICATION, SHAPE_QUALIFICATION } from "../lib/site";
import { ProductDocumentDownloads } from "./ProductDocumentDownloads";
import { useLightbox, Watermark } from "./Lightbox";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "./ui/tabs";
import { Accordion, AccordionItem, AccordionTrigger, AccordionContent } from "./ui/accordion";
import { Collapsible, CollapsibleTrigger, CollapsibleContent } from "./ui/collapsible";

const API = process.env.REACT_APP_BACKEND_URL;

const slug = (s) => s.toLowerCase().replace(/[\s/()]+/g, "-").replace(/-+$/g, "");

const COMMERCIAL = [
  {
    icon: Boxes,
    title: "MOQ",
    copy: "Typical MOQ starts from 1 x 20FT container.",
  },
  {
    icon: Container,
    title: "Loading Capacity",
    copy: "Around 18 MT per 20FT and 26 MT per 40FT container, depending on product type and packaging configuration.",
  },
  {
    icon: Gauge,
    title: "Production Capacity",
    copy: PARTNER_CAPACITY,
  },
  {
    icon: BadgeDollarSign,
    title: "Payment Terms",
    copy: "100% T/T in advance, or 50% T/T advance with the remaining 50% before container stuffing. Final payment terms are confirmed based on product, order volume, and supplier arrangement.",
  },
  {
    icon: Clock,
    title: "Lead Time",
    copy: "3\u20134 weeks after order confirmation and payment, depending on product specification and manufacturing schedule.",
  },
  {
    icon: Ship,
    title: "Incoterms",
    copy: "FOB \u2014 Indonesia. CIF \u2014 upon request. FOB and CIF quotations are available depending on product, destination port and current shipping conditions.",
  },
];

const DOC_TYPE_LABELS = {
  coa: "Certificate of Analysis (COA)",
  msds: "SDS / MSDS",
  specification: "Specification Sheet",
  packaging: "Packaging Sheet",
  other: "Document",
};

function ProductDocuments({ slug: productSlug }) {
  const [docs, setDocs] = useState(null);

  useEffect(() => {
    axios
      .get(`${API}/api/products/${productSlug}/documents`)
      .then((res) => setDocs(res.data.documents))
      .catch(() => setDocs([]));
  }, [productSlug]);

  return (
    <div data-testid="product-documents">
      {docs === null ? null : docs.length > 0 ? (
        <div className="space-y-px bg-white/10 border border-white/10">
          {docs.map((doc) => (
            <a
              key={doc.id}
              href={`${API}/api/documents/${doc.id}/download`}
              target="_blank"
              rel="noopener noreferrer"
              data-testid={`doc-download-${doc.doc_type}-${doc.id}`}
              className="group flex items-center justify-between gap-4 bg-charcoal px-6 sm:px-8 py-5 hover:bg-white/[0.05] transition-colors duration-300"
            >
              <span className="flex items-center gap-4">
                <FileText className="w-5 h-5 text-clay shrink-0" />
                <span>
                  <span className="block text-sm font-semibold text-offwhite">{doc.label}</span>
                  <span className="block text-xs text-offwhite/45 mt-0.5">
                    {DOC_TYPE_LABELS[doc.doc_type] || "Document"} — {(doc.size / 1024).toFixed(0)} KB
                  </span>
                </span>
              </span>
              <FileDown className="w-4 h-4 text-offwhite/50 group-hover:text-clay transition-colors duration-300" />
            </a>
          ))}
        </div>
      ) : (
        <div className="space-y-4">
          {["COA available upon request.", "SDS / MSDS available where applicable.", "Specification sheet available upon request."].map(
            (line) => (
              <p key={line} data-testid={`product-document-note-${slug(line)}`} className="flex items-center gap-4 text-sm text-offwhite/55 border-b border-white/10 pb-4">
                <FileText className="w-4 h-4 text-clay shrink-0" />
                {line}
              </p>
            )
          )}
        </div>
      )}
      <p data-testid="product-documentation-disclaimer" className="mt-6 text-xs text-offwhite/40 leading-relaxed">
        Documentation availability depends on the product and manufacturing partner. Only verified documents are
        shared with buyers.
      </p>
    </div>
  );
}

function StatChip({ label, value }) {
  return (
    <div
      data-testid={`stat-chip-${slug(label)}`}
      className="bg-offwhite p-4 sm:p-5 flex flex-col gap-1.5"
    >
      <span className="text-[9px] font-bold uppercase tracking-[0.2em] text-charcoal/40">{label}</span>
      <span className="font-display text-base sm:text-lg font-bold tracking-tight leading-tight">{value}</span>
    </div>
  );
}

function SummarySection({ config, waQuoteHref }) {
  const openLightbox = useLightbox();
  return (
    <section className="pt-40 pb-16 sm:pb-20">
      <div className="max-w-[1440px] mx-auto px-5 sm:px-10">
        <div className="flex items-center gap-4">
          <span className="font-display text-xs font-semibold tracking-[0.3em] text-forest">{config.label}</span>
          <span className="h-px w-12 bg-charcoal/25" />
          <span className="text-xs font-bold uppercase tracking-[0.25em] text-charcoal/60">{config.category}</span>
        </div>

        <div data-testid="product-summary-card" className="mt-8 grid lg:grid-cols-12 gap-10 lg:gap-14 items-start">
          <Reveal className="lg:col-span-5">
            <div
              className="relative bg-[#EAE7DF] overflow-hidden cursor-zoom-in"
              data-testid="product-hero-image-wrap"
              onClick={() => openLightbox([{ src: config.heroImage, alt: config.heroAlt }], 0)}
            >
              <img
                src={config.heroImage}
                alt={config.heroAlt}
                data-testid="product-hero-image"
                className="w-full h-72 sm:h-[420px] lg:h-[460px] object-contain p-8 sm:p-12"
              />
              <span className="absolute top-4 left-4 text-[9px] font-bold uppercase tracking-[0.2em] text-charcoal/60 bg-offwhite/80 backdrop-blur-sm px-3 py-1.5">
                Reference Image
              </span>
              <Watermark />
            </div>
          </Reveal>

          <Reveal delay={0.12} className="lg:col-span-7">
            <h1 data-testid="product-heading" className="font-display text-4xl sm:text-5xl lg:text-6xl font-black tracking-tight leading-[1.0]">
              {config.h1}
            </h1>
            <p data-testid="product-tagline" className="mt-4 font-display text-lg sm:text-xl font-semibold text-forest">{config.tagline}</p>

            <div
              data-testid="product-key-specs"
              className="mt-8 grid grid-cols-2 sm:grid-cols-5 gap-px bg-charcoal/10 border border-charcoal/10"
            >
              {config.keySpecs.map((spec) => (
                <StatChip key={spec.label} label={spec.label} value={spec.value} />
              ))}
            </div>
            <p data-testid="product-size-note" className="mt-3 text-xs text-charcoal/50 leading-relaxed">
              <span data-testid="product-summary-specification-note" className="block">{config.specificationNote}</span>
              <span data-testid="product-summary-burning-note" className="block">{config.burningTimeNote}</span>
              <span data-testid="product-summary-shape-note" className="block">{SHAPE_QUALIFICATION} Packaging can be customized on request.</span>
            </p>

            <div className="mt-8 flex flex-wrap gap-3">
              <a
                href={waQuoteHref}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="product-whatsapp-cta"
                className="group inline-flex items-center gap-2 bg-[#25D366] hover:bg-[#1fb857] text-white font-semibold px-6 py-3.5 text-sm tracking-wide transition-colors duration-300"
              >
                <MessageCircle className="w-4 h-4" fill="white" />
                Quote via WhatsApp
              </a>
              <ProductDocumentDownloads />
            </div>
          </Reveal>
        </div>
      </div>
    </section>
  );
}

function GalleryStrip({ gallery }) {
  const openLightbox = useLightbox();
  const cols = gallery.length > 6 ? "md:grid-cols-3" : "md:grid-cols-5";
  const lightboxImages = gallery.map((img) => ({ src: img.src, alt: img.alt, caption: img.caption }));
  return (
    <Stagger data-testid="product-gallery" className={`mt-4 grid grid-cols-2 gap-3 ${cols}`} gap={0.07}>
      {gallery.map((img, i) => (
        <StaggerItem key={`${img.src}-${img.caption}`}>
          <div
            className="group relative overflow-hidden h-40 sm:h-48 cursor-zoom-in"
            data-testid={`product-gallery-tile-${i + 1}`}
            onClick={() => openLightbox(lightboxImages, i)}
          >
            <img
              src={img.src}
              alt={img.alt}
              loading="lazy"
              className="w-full h-full object-cover img-spotlight transition-transform duration-700 ease-out group-hover:scale-105"
            />
            <span className="absolute bottom-0 inset-x-0 text-[9px] font-bold uppercase tracking-[0.2em] text-offwhite/90 bg-[#111111]/60 backdrop-blur-sm px-3 py-2">
              {img.caption}
            </span>
            <Watermark />
          </div>
        </StaggerItem>
      ))}
    </Stagger>
  );
}

function OverviewContent({ config }) {
  return (
    <div data-testid="product-tab-content-overview" className="grid lg:grid-cols-12 gap-14">
      <div className="lg:col-span-7 space-y-5">
        {config.overview.map((p, i) => (
          <p
            key={p}
            className={
              i === 0
                ? "text-lg text-charcoal/80 leading-relaxed font-medium"
                : "text-base text-charcoal/65 leading-relaxed"
            }
          >
            {p}
          </p>
        ))}
      </div>
      <div className="lg:col-span-5">
        <h2 data-testid="product-applications-heading" className="font-body text-[10px] font-bold uppercase tracking-[0.25em] text-forest mb-4">Product Application</h2>
        <div className="border-t border-charcoal/10">
          {config.applications.map((app, i) => (
            <div key={app} data-testid={`application-${i + 1}`} className="flex items-center gap-5 py-4 border-b border-charcoal/10">
              <span className="font-display text-xs font-semibold tracking-[0.3em] text-charcoal/35">
                {String(i + 1).padStart(2, "0")}
              </span>
              <span className="font-display text-base font-semibold tracking-tight">{app}</span>
            </div>
          ))}
        </div>
      </div>
      <div className="lg:col-span-12">
        <h2 data-testid="product-gallery-heading" className="font-body text-[10px] font-bold uppercase tracking-[0.25em] text-forest mb-1">Gallery</h2>
        <GalleryStrip gallery={config.gallery} />
      </div>
    </div>
  );
}

function SizeOptionsBlock({ sizeOptions }) {
  const openLightbox = useLightbox();
  const lightboxImages = sizeOptions
    .filter((opt) => opt.image)
    .map((opt) => ({ src: opt.image, alt: opt.alt, caption: `${opt.name} — ${opt.size}` }));
  return (
    <div data-testid="size-options-section">
      <h3 data-testid="product-sizes-heading" className="font-body text-[10px] font-bold uppercase tracking-[0.25em] text-forest mb-4">
        Available Shapes &amp; Base Sizes
      </h3>
      <Stagger data-testid="product-standard-sizes" className="grid sm:grid-cols-2 lg:grid-cols-3 gap-px bg-charcoal/10 border border-charcoal/10" gap={0.06}>
        {sizeOptions.map((opt, i) => (
          <StaggerItem key={opt.name}>
            <div data-testid={`size-option-${i + 1}`} className="bg-offwhite hover:bg-sand p-5 flex flex-col gap-4 h-full transition-colors duration-300">
              {opt.image && (
                <div
                  className="relative h-28 bg-[#EAE7DF] overflow-hidden cursor-zoom-in"
                  onClick={() =>
                    openLightbox(
                      lightboxImages,
                      lightboxImages.findIndex((im) => im.src === opt.image)
                    )
                  }
                >
                  <img src={opt.image} alt={opt.alt} className="w-full h-full object-contain p-3" />
                  <Watermark className="text-[8px] bottom-0.5 right-1" />
                </div>
              )}
              <div>
                <span className="font-display text-sm font-black tracking-tight block">{opt.name}</span>
                <span className="text-xs text-charcoal/60 font-medium mt-1 block">{opt.size}</span>
              </div>
            </div>
          </StaggerItem>
        ))}
      </Stagger>
    </div>
  );
}

function SpecsContent({ config }) {
  return (
    <div data-testid="product-tab-content-specs" className="space-y-14">
      <div className="bg-charcoal text-offwhite p-6 sm:p-10 tech-grid">
        <h2 data-testid="product-specifications-heading" className="font-body text-[10px] font-bold uppercase tracking-[0.25em] text-clay mb-2">
          Typical Specifications (Supplier Reference)
        </h2>
        <p data-testid="product-specifications-disclaimer" className="text-sm text-offwhite/55 leading-relaxed max-w-xl mb-6">
          {config.specificationNote} {config.specificationNote !== SPECIFICATION_QUALIFICATION && SPECIFICATION_QUALIFICATION}
          <span data-testid="product-specifications-burning-note" className="block">{config.burningTimeNote}</span>
        </p>
        <Stagger data-testid="product-specs-table" className="border border-white/10" gap={0.03}>
          {config.specs.map(([label, value]) => {
            const isPending =
              value.toLowerCase().includes("upon request") ||
              value.toLowerCase().includes("to be confirmed") ||
              value.toLowerCase().includes("tbd");
            return (
              <StaggerItem key={label}>
                <div
                  data-testid={`spec-${slug(label)}`}
                  className="flex flex-col sm:flex-row sm:items-center justify-between gap-1 sm:gap-6 px-6 sm:px-8 py-4 sm:py-5 border-b border-white/10 last:border-b-0 hover:bg-white/[0.04] transition-colors duration-300"
                >
                  <span className="text-xs font-bold uppercase tracking-[0.2em] text-offwhite/45">{label}</span>
                  <span
                    className={`font-display text-base sm:text-lg font-semibold sm:text-right ${
                      isPending ? "text-offwhite/45 italic" : ""
                    }`}
                  >
                    {value}
                  </span>
                </div>
              </StaggerItem>
            );
          })}
        </Stagger>
      </div>

      {config.sizeOptions && <SizeOptionsBlock sizeOptions={config.sizeOptions} />}

      <div>
        <h3 data-testid="product-shapes-heading" className="font-body text-[10px] font-bold uppercase tracking-[0.25em] text-forest mb-4">Shapes Offered</h3>
        <Stagger className="grid grid-cols-2 gap-px bg-charcoal/10 border border-charcoal/10" gap={0.05}>
          {config.shapes.map((shape) => (
            <StaggerItem key={shape}>
              <div
                data-testid={`shape-${slug(shape)}`}
                className="bg-offwhite p-6 h-full flex items-center gap-4 hover:bg-sand transition-colors duration-300"
              >
                <Square className="w-5 h-5 text-forest shrink-0" strokeWidth={1.5} />
                <span className="font-display text-sm sm:text-base font-bold tracking-tight">{shape}</span>
              </div>
            </StaggerItem>
          ))}
        </Stagger>
      </div>
    </div>
  );
}

function MoldingCollapsible({ moldingOptions }) {
  const [open, setOpen] = useState(false);
  if (!moldingOptions) return null;
  return (
    <Collapsible open={open} onOpenChange={setOpen} className="mt-10" data-testid="molding-options-section">
      <CollapsibleTrigger
        data-testid="molding-toggle"
        className="w-full flex items-center justify-between gap-4 border border-charcoal/15 px-5 py-4 hover:bg-sand transition-colors duration-300 text-left"
      >
        <span className="text-xs font-bold uppercase tracking-[0.2em] text-forest">Custom Mold Available</span>
        <ChevronDown className={`w-4 h-4 text-forest shrink-0 transition-transform duration-300 ${open ? "rotate-180" : ""}`} />
      </CollapsibleTrigger>
      <CollapsibleContent data-testid="product-molding-reference" className="border-x border-b border-charcoal/15">
        <p data-testid="product-molding-disclaimer" className="px-5 pt-4 text-sm text-charcoal/60 leading-relaxed">
          {SHAPE_QUALIFICATION}
        </p>
        <div className="mt-4">
          {moldingOptions.map((row, i) => (
            <div
              key={row.shape}
              data-testid={`molding-option-${i + 1}`}
              className="flex flex-col sm:flex-row sm:items-center justify-between gap-1 sm:gap-6 bg-offwhite hover:bg-sand px-5 sm:px-6 py-4 border-t border-charcoal/10 first:border-t-0 transition-colors duration-300"
            >
              <span className="text-xs font-bold uppercase tracking-[0.15em] text-charcoal/50">{row.shape}</span>
              <span className="font-display text-sm font-semibold sm:text-right whitespace-nowrap sm:whitespace-normal">
                {row.sizes}
              </span>
            </div>
          ))}
        </div>
      </CollapsibleContent>
    </Collapsible>
  );
}

function PackagingContent({ config, quoteUrl }) {
  return (
    <div data-testid="product-tab-content-packaging">
      <h2 data-testid="product-packaging-heading" className="font-body text-[10px] font-bold uppercase tracking-[0.25em] text-forest mb-2">Packaging Options</h2>
      <p className="text-sm text-charcoal/60 leading-relaxed max-w-xl">
        Packaging configuration depends on product, buyer requirements and manufacturing partner. Retail, bulk,
        custom and private-label packaging can be requested.
      </p>
      <Stagger data-testid="product-packaging" className="mt-8 space-y-px bg-charcoal/10 border border-charcoal/10" gap={0.06}>
        {config.packaging.map((pack) => (
          <StaggerItem key={pack.size}>
            <div
              data-testid={`packaging-${slug(pack.size)}`}
              className="bg-offwhite hover:bg-sand p-6 flex items-start gap-5 transition-colors duration-300"
            >
              <Package className="w-6 h-6 text-forest shrink-0 mt-0.5" strokeWidth={1.5} />
              <div>
                <span className="font-display text-lg font-black tracking-tight">{pack.size}</span>
                <p className="mt-1 text-sm text-charcoal/60 leading-relaxed">{pack.copy}</p>
              </div>
            </div>
          </StaggerItem>
        ))}
      </Stagger>
      <Reveal delay={0.1} className="mt-6">
        <Link
          to={`${quoteUrl}&request=packaging`}
          data-testid="product-packaging-cta"
          className="group inline-flex items-center gap-2 text-sm font-semibold text-forest hover:text-clay transition-colors duration-300"
        >
          Discuss Packaging Requirements
          <ArrowUpRight className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
        </Link>
      </Reveal>
      <MoldingCollapsible moldingOptions={config.moldingOptions} />
    </div>
  );
}

function CommercialContent() {
  return (
    <div data-testid="product-tab-content-commercial" className="bg-sand tech-grid-light p-6 sm:p-10 -mx-1">
      <p className="text-[10px] font-bold uppercase tracking-[0.25em] text-forest mb-2">Commercial Terms</p>
      <h2 className="font-display text-2xl sm:text-3xl font-bold tracking-tight">MOQ, Payment, Lead Time &amp; Shipping</h2>
      <Stagger data-testid="product-commercial-terms" className="mt-10 grid sm:grid-cols-2 lg:grid-cols-3 gap-px bg-charcoal/10 border border-charcoal/10" gap={0.06}>
        {COMMERCIAL.map((item) => (
          <StaggerItem key={item.title} className={item.title === "Payment Terms" ? "sm:col-span-2" : ""}>
            <div
              data-testid={`commercial-${slug(item.title)}`}
              className="bg-sand hover:bg-offwhite p-8 h-full flex flex-col gap-5 transition-colors duration-300"
            >
              <div className="flex items-center justify-between">
                <item.icon className="w-6 h-6 text-forest" strokeWidth={1.5} />
                <span className="text-[10px] font-bold uppercase tracking-[0.25em] text-charcoal/35">
                  {item.title}
                </span>
              </div>
              <p className="text-sm text-charcoal/70 leading-relaxed">{item.copy}</p>
            </div>
          </StaggerItem>
        ))}
        <StaggerItem>
          <Link
            to="/commercial-terms"
            data-testid="commercial-terms-link"
            className="group bg-forest text-offwhite p-8 h-full flex flex-col justify-between gap-8 hover:bg-moss transition-colors duration-300"
          >
            <span className="font-display text-xl font-bold tracking-tight leading-snug">
              Full commercial terms
            </span>
            <span className="inline-flex items-center gap-2 text-sm font-semibold text-offwhite/80 group-hover:text-offwhite transition-colors duration-300">
              View details
              <ArrowUpRight className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
            </span>
          </Link>
        </StaggerItem>
      </Stagger>
    </div>
  );
}

const TAB_ITEMS = [
  { value: "overview", label: "Overview" },
  { value: "specs", label: "Specifications" },
  { value: "packaging", label: "Packaging" },
  { value: "commercial", label: "Commercial Terms" },
];

function useIsMobile(breakpoint = 640) {
  const [isMobile, setIsMobile] = useState(
    typeof window !== "undefined" ? window.innerWidth < breakpoint : false
  );
  useEffect(() => {
    const mql = window.matchMedia(`(max-width: ${breakpoint - 1}px)`);
    const onChange = () => setIsMobile(mql.matches);
    onChange();
    mql.addEventListener("change", onChange);
    return () => mql.removeEventListener("change", onChange);
  }, [breakpoint]);
  return isMobile;
}

function ProductTabsSection({ config, quoteUrl }) {
  const isMobile = useIsMobile();
  const contentFor = (value) => {
    if (value === "overview") return <OverviewContent config={config} />;
    if (value === "specs") return <SpecsContent config={config} />;
    if (value === "packaging") return <PackagingContent config={config} quoteUrl={quoteUrl} />;
    return <CommercialContent />;
  };

  return (
    <section data-testid="product-tabs-section" className="pb-20 sm:pb-28">
      <div className="max-w-[1440px] mx-auto px-5 sm:px-10">
        {isMobile ? (
          <Accordion type="single" collapsible defaultValue="overview" data-testid="product-accordion-mobile">
            {TAB_ITEMS.map((t) => (
              <AccordionItem key={t.value} value={t.value} data-testid={`product-accordion-item-${t.value}`} className="border-charcoal/10">
                <AccordionTrigger
                  data-testid={`product-accordion-trigger-${t.value}`}
                  className="text-sm font-bold uppercase tracking-wide text-charcoal py-5"
                >
                  {t.label}
                </AccordionTrigger>
                <AccordionContent>{contentFor(t.value)}</AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        ) : (
          <Tabs defaultValue="overview" data-testid="product-tabs-desktop">
            <TabsList className="w-full h-auto justify-start gap-1 bg-transparent p-0 border-b border-charcoal/10 rounded-none overflow-x-auto">
              {TAB_ITEMS.map((t) => (
                <TabsTrigger
                  key={t.value}
                  value={t.value}
                  data-testid={`product-tab-trigger-${t.value}`}
                  className="rounded-none border-b-2 border-transparent data-[state=active]:border-forest data-[state=active]:bg-transparent data-[state=active]:shadow-none px-5 py-4 text-sm font-semibold uppercase tracking-wide text-charcoal/50 data-[state=active]:text-charcoal whitespace-nowrap"
                >
                  {t.label}
                </TabsTrigger>
              ))}
            </TabsList>
            {TAB_ITEMS.map((t) => (
              <TabsContent key={t.value} value={t.value} className="mt-10 focus-visible:outline-none">
                {contentFor(t.value)}
              </TabsContent>
            ))}
          </Tabs>
        )}
      </div>
    </section>
  );
}

function DocsSection({ slug: productSlug }) {
  return (
    <section data-testid="product-docs-section" className="bg-charcoal text-offwhite py-20 sm:py-28">
      <div className="max-w-[1440px] mx-auto px-5 sm:px-10 grid lg:grid-cols-12 gap-12">
        <div className="lg:col-span-5">
          <p className="text-[10px] font-bold uppercase tracking-[0.25em] text-clay mb-2">Documentation</p>
          <Reveal>
            <h2 data-testid="product-documentation-heading" className="font-display text-3xl sm:text-4xl font-bold tracking-tight">Product Documentation</h2>
            <p data-testid="product-documentation-intro" className="mt-4 text-sm text-offwhite/55 leading-relaxed">
              Verified product documents are shared for buyer review. If a document is not listed here, it can be
              requested during the quotation process.
            </p>
          </Reveal>
        </div>
        <Reveal delay={0.15} className="lg:col-span-7">
          <ProductDocuments slug={productSlug} />
        </Reveal>
      </div>
    </section>
  );
}

function BottomCtaSection({ quoteUrl }) {
  return (
    <section className="py-20 sm:py-28">
      <div className="max-w-[1440px] mx-auto px-5 sm:px-10 grid md:grid-cols-2 gap-px bg-charcoal/10 border border-charcoal/10">
        <Reveal className="bg-offwhite p-10 sm:p-14">
          <h2 className="font-display text-3xl font-bold tracking-tight">Request a Sample</h2>
          <p className="mt-4 text-sm text-charcoal/60 leading-relaxed">
            Sample availability and shipping arrangements depend on the selected product and manufacturing partner.
          </p>
          <Link
            to={`${quoteUrl}&request=sample`}
            data-testid="product-sample-bottom-cta"
            className="group mt-8 inline-flex items-center gap-2 bg-charcoal hover:bg-forest text-offwhite font-semibold px-7 py-4 text-sm tracking-wide transition-colors duration-300"
          >
            Request a Sample
            <ArrowUpRight className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
          </Link>
        </Reveal>
        <Reveal delay={0.1} className="bg-forest text-offwhite p-10 sm:p-14">
          <h2 className="font-display text-3xl font-bold tracking-tight">Request a Quote</h2>
          <p className="mt-4 text-sm text-offwhite/70 leading-relaxed">
            Share your specification, quantity, packaging and destination port for an FOB or CIF quotation.
          </p>
          <Link
            to={quoteUrl}
            data-testid="product-quote-bottom-cta"
            className="group mt-8 inline-flex items-center gap-2 bg-offwhite text-charcoal hover:bg-clay hover:text-offwhite font-semibold px-7 py-4 text-sm tracking-wide transition-colors duration-300"
          >
            Request a Quote
            <ArrowUpRight className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
          </Link>
        </Reveal>
      </div>
    </section>
  );
}

function StickyQuoteCTA({ waHref }) {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const onScroll = () => setVisible(window.scrollY > 420);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <div
      data-testid="product-sticky-whatsapp-cta"
      className={`fixed bottom-20 sm:bottom-6 left-4 sm:left-6 z-40 transition-all duration-300 ${
        visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4 pointer-events-none"
      }`}
    >
      <a
        href={waHref}
        target="_blank"
        rel="noopener noreferrer"
        data-testid="product-sticky-whatsapp-link"
        className="inline-flex items-center gap-2 bg-[#25D366] hover:bg-[#1fb857] text-white font-semibold px-5 py-3 sm:px-6 sm:py-3.5 text-xs sm:text-sm tracking-wide shadow-lg shadow-black/25 whitespace-nowrap"
      >
        <MessageCircle className="w-4 h-4 shrink-0" fill="white" />
        <span className="hidden sm:inline">Quote via WhatsApp</span>
        <span className="sm:hidden">Quote via WhatsApp</span>
      </a>
    </div>
  );
}

export default function ProductPage({ config }) {
  const quoteUrl = `/request-a-quote?product=${encodeURIComponent(config.quoteProduct)}`;
  const waQuoteHref = waLink(
    `Hello HAQILA, I'm interested in ${config.quoteProduct}. I'd like to request a quote.`
  );

  return (
    <>
      <SEO />
      <div data-testid={`${config.slug}-page`} className="bg-offwhite">
        <SummarySection config={config} waQuoteHref={waQuoteHref} />
        <ProductTabsSection config={config} quoteUrl={quoteUrl} />
        <DocsSection slug={config.slug} />
        <BottomCtaSection quoteUrl={quoteUrl} />
      </div>
      <StickyQuoteCTA waHref={waQuoteHref} />
    </>
  );
}
